import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarBlank, MapPin, Trophy, Users, PencilSimple, Trash, ListChecks, DownloadSimple, Printer } from '@phosphor-icons/react';
import { LastSyncedPill } from '../LastSyncedPill';
import { SaveOfflineButton } from './SaveOfflineButton';

export const ShowHeader = ({ show, isAdmin, onStateChange, onDelete }) => {
  const navigate = useNavigate();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  return (
    <>
      <div className="mb-6 sm:mb-8 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="text-2xl sm:text-4xl font-black text-white mb-1 sm:mb-2" style={{fontFamily: 'Outfit'}} data-testid="show-name">{show.name}</h1>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-[#9E9E9E] text-xs sm:text-base">{show.description}</p>
              <LastSyncedPill />
              <SaveOfflineButton show={show} />
            </div>
          </div>
          <span data-testid="show-state-badge" className={`self-start px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-semibold text-xs sm:text-sm flex-shrink-0 ${
            show.state === 'LIVE' ? 'bg-[#00D4FF]/20 text-[#00D4FF]' :
            show.state === 'PUBLISHED' ? 'bg-[#34C759]/20 text-[#34C759]' :
            show.state === 'DRAFT' ? 'bg-[#FACC15]/20 text-[#FACC15]' :
            show.state === 'COMPLETED' ? 'bg-[#0096FF]/20 text-[#0096FF]' :
            'bg-[#9E9E9E]/20 text-[#9E9E9E]'
          }`}>{show.state}</span>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
            <CalendarBlank size={20} className="text-[#0096FF] flex-shrink-0" weight="duotone" />
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs text-[#9E9E9E]">Dates</p>
              <p className="text-white font-semibold text-xs sm:text-sm truncate">
                {new Date(show.start_date).toLocaleDateString()} - {new Date(show.end_date).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
            <MapPin size={20} className="text-[#00D4FF] flex-shrink-0" weight="duotone" />
            <div className="min-w-0">
              <p className="text-[10px] sm:text-xs text-[#9E9E9E]">Location</p>
              <p className="text-white font-semibold text-xs sm:text-sm truncate">{show.location}</p>
            </div>
          </div>
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
            <Trophy size={20} className="text-[#34C759] flex-shrink-0" weight="duotone" />
            <div>
              <p className="text-[10px] sm:text-xs text-[#9E9E9E]">Classes</p>
              <p className="text-white font-semibold text-xs sm:text-sm">{show.classes.length} Events</p>
            </div>
          </div>
          <div className="bg-[#161616] border border-[#262626] rounded-lg p-3 sm:p-4 flex items-center gap-2 sm:gap-3">
            <Users size={20} className="text-[#C0C0C0] flex-shrink-0" weight="duotone" />
            <div>
              <p className="text-[10px] sm:text-xs text-[#9E9E9E]">Arenas</p>
              <p className="text-white font-semibold text-xs sm:text-sm">{show.arenas.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Show Flyer / Showbill */}
      {show.showbill_url && (
        <div className="mb-6 sm:mb-8" data-testid="show-flyer-header">
          <div className="bg-[#0A0A0A] border border-[#262626] rounded-lg overflow-hidden">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}${show.showbill_url}`}
              alt={`${show.name} flyer`}
              className="w-full max-h-[500px] object-contain"
              data-testid="show-flyer-image"
              onError={(e) => { e.target.parentElement.parentElement.style.display = 'none'; }}
            />
            <div className="px-4 py-3 bg-[#161616] border-t border-[#262626] flex flex-wrap justify-end gap-2">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/shows/${show.id}/printable-entry-form.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF9500]/15 hover:bg-[#FF9500]/25 text-[#FF9500] border border-[#FF9500]/30 rounded-md text-sm font-semibold transition-colors"
                data-testid="download-entry-form-btn"
                title="Print-ready entry form (mail-in) with flyer + classes + fillable fields"
              >
                <DownloadSimple size={14} weight="bold" /> Entry Form PDF
              </a>
              <button
                type="button"
                onClick={() => {
                  // Open the PDF in a new tab and trigger the browser's
                  // print dialog. Same source of truth as the download —
                  // the new-tab fallback keeps mobile Safari behaving.
                  const url = `${process.env.REACT_APP_BACKEND_URL}/api/shows/${show.id}/printable-entry-form.pdf`;
                  const w = window.open(url, '_blank');
                  if (w) {
                    // Wait for the embedded PDF viewer to render, then
                    // ask the new window to print. Some browsers block
                    // cross-origin print() — falling back gracefully.
                    setTimeout(() => { try { w.focus(); w.print(); } catch { /* user can Cmd+P */ } }, 1500);
                  }
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#34C759]/15 hover:bg-[#34C759]/25 text-[#34C759] border border-[#34C759]/30 rounded-md text-sm font-semibold transition-colors"
                data-testid="print-entry-form-btn"
                title="Open the entry form in a new tab and print"
              >
                <Printer size={14} weight="bold" /> Print Entry Form
              </button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}${show.showbill_url}`}
                download={`${show.name.replace(/[^a-zA-Z0-9]/g, '_')}_flyer`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 bg-[#0096FF]/15 hover:bg-[#0096FF]/25 text-[#0096FF] border border-[#0096FF]/30 rounded-md text-sm font-semibold transition-colors"
                data-testid="download-flyer-btn"
              >
                <DownloadSimple size={14} weight="bold" /> Download Flyer
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Entry-form buttons standalone — also visible when no show bill
          is uploaded yet, so producers / riders can still grab the
          mail-in form. When a show bill IS uploaded, the same buttons
          live inside the flyer card above and this block is skipped. */}
      {!show.showbill_url && (
        <div className="mb-6 sm:mb-8 flex flex-wrap gap-2" data-testid="entry-form-buttons-row">
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/shows/${show.id}/printable-entry-form.pdf`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#FF9500]/15 hover:bg-[#FF9500]/25 text-[#FF9500] border border-[#FF9500]/30 rounded-md text-sm font-semibold transition-colors"
            data-testid="download-entry-form-btn-standalone"
          >
            <DownloadSimple size={14} weight="bold" /> Entry Form PDF
          </a>
          <button
            type="button"
            onClick={() => {
              const url = `${process.env.REACT_APP_BACKEND_URL}/api/shows/${show.id}/printable-entry-form.pdf`;
              const w = window.open(url, '_blank');
              if (w) {
                setTimeout(() => { try { w.focus(); w.print(); } catch { /* user can Cmd+P */ } }, 1500);
              }
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#34C759]/15 hover:bg-[#34C759]/25 text-[#34C759] border border-[#34C759]/30 rounded-md text-sm font-semibold transition-colors"
            data-testid="print-entry-form-btn-standalone"
          >
            <Printer size={14} weight="bold" /> Print Entry Form
          </button>
        </div>
      )}

      {isAdmin && (
        <div className="mb-6 sm:mb-8 bg-[#161616] border border-[#262626] rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h3 className="text-base sm:text-lg font-bold text-white" style={{fontFamily: 'Outfit'}}>Manage Show State</h3>
            <div className="flex gap-2 self-start">
              <button
                onClick={() => navigate(`/shows/${show.id}/manage-entries`)}
                data-testid="manage-entries-btn"
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#34C759]/10 border border-[#34C759]/30 text-[#34C759] font-semibold rounded-md hover:bg-[#34C759]/20 transition-colors text-xs sm:text-sm"
              >
                <ListChecks size={14} weight="bold" /> Manage Entries
              </button>
              <button
                onClick={() => navigate(`/shows/${show.id}/edit`)}
                data-testid="edit-show-btn"
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#FF9500]/10 border border-[#FF9500]/30 text-[#FF9500] font-semibold rounded-md hover:bg-[#FF9500]/20 transition-colors text-xs sm:text-sm"
              >
                <PencilSimple size={14} weight="bold" /> Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                data-testid="delete-show-btn"
                className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] font-semibold rounded-md hover:bg-[#FF3B30]/20 transition-colors text-xs sm:text-sm"
              >
                <Trash size={14} weight="bold" /> Delete
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3 sm:flex-wrap">
            {['DRAFT', 'PUBLISHED', 'LIVE', 'COMPLETED'].map((state) => {
              const colors = {
                DRAFT: { bg: '#FACC15', text: '#FACC15' },
                PUBLISHED: { bg: '#34C759', text: '#34C759' },
                LIVE: { bg: '#00D4FF', text: '#00D4FF' },
                COMPLETED: { bg: '#0096FF', text: '#0096FF' }
              };
              const c = colors[state];
              return (
                <button
                  key={state}
                  onClick={() => onStateChange(state)}
                  disabled={show.state === state}
                  data-testid={`state-${state.toLowerCase()}-btn`}
                  className="px-3 sm:px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs sm:text-sm font-semibold"
                  style={{
                    backgroundColor: `${c.bg}15`,
                    border: `1px solid ${c.bg}33`,
                    color: c.text
                  }}
                >
                  {state === 'PUBLISHED' ? 'Publish' : state === 'LIVE' ? 'Go Live' : state === 'COMPLETED' ? 'Complete' : 'Draft'}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="bg-[#161616] border border-[#262626] rounded-xl p-6 max-w-md w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-2" style={{fontFamily: 'Outfit'}}>Delete Show</h3>
            <p className="text-[#9E9E9E] text-sm mb-1">Are you sure you want to delete <span className="text-white font-semibold">"{show.name}"</span>?</p>
            <p className="text-[#FF3B30]/80 text-xs mb-6">This will permanently remove the show, all entries, scores, payouts, and reservations. This action cannot be undone.</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                data-testid="delete-cancel-btn"
                className="px-4 py-2 rounded-md text-sm font-semibold text-[#9E9E9E] bg-[#262626] hover:bg-[#333] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setShowDeleteConfirm(false); onDelete(); }}
                data-testid="delete-confirm-btn"
                className="px-4 py-2 rounded-md text-sm font-semibold text-white bg-[#FF3B30] hover:bg-[#FF3B30]/80 transition-colors"
              >
                Delete Show
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
